import React, { useMemo, useState } from "react"
import { observer } from "mobx-react"
import { useFormStore } from "components/ProjectSpecificComponents/Contexts/FormStoreContext"
import ToolFieldProperties from "../ToolFieldProperties/ToolFieldProperties"
import { TReactSelectOption } from "components/ProjectSpecificComponents/Interfaces/ExternalComponentsInterfaces"
import getFieldDataByMainFieldKey from "./NewRequestForm/Functions/getFieldDataByMainFieldKey"
import ToolFieldPropertiesHeaderWithContent from "../ToolFieldPropertiesHeaderWithContent"
import createReactSelectOptions from "../ToolFieldProperties/createReactSelectOptions"
import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import { useTranslation } from "react-i18next"
import { REACT_SELECT_FAIL_FALLBACK_OPTIONS } from "components/ProjectSpecificComponents/Constants/GLOBAL_CONSTANTS"
import { TItemOfTypeOtherValues } from "components/ProjectSpecificComponents/Interfaces/DataInterface"
import { useStore } from "components/ProjectSpecificComponents/Contexts/GlobalStoreContext"
import { IToolTipText } from "components/ProjectSpecificComponents/Interfaces/FormsInterface"

interface Props {}

/**
 * Renders potentially multiple tool's field properties (inputs etc.)
 * with their headers.
 */
const NewRequestForm = observer(({}: Props) => {
	const formStore = useFormStore()
	const store = useStore()
	const { selectedFormsConfig } = formStore
	const { instanceAnonymousUsers } = store
	const { t } = useTranslation()

	const [isSectionHidden] = useState<{ [k: string]: boolean }>({})
	const [refreshCount, refresh] = useState(0)

	const toggleIsSectionHidden = (fieldKey: string) => {
		isSectionHidden[fieldKey] = !isSectionHidden[fieldKey]
		refresh((count) => count + 1)
	}

	const predicateFieldKeysOptions: TReactSelectOption[] = useMemo(() => {
		if (selectedFormsConfig?.newRequestForm == null) return REACT_SELECT_FAIL_FALLBACK_OPTIONS

		const selectedPicklistFields = selectedFormsConfig.newRequestForm.filter(
			({ fieldType }) => fieldType === "PICKLIST" || fieldType === "MULTI_PICKLIST"
		)
		return createReactSelectOptions(selectedPicklistFields, "mainFieldKey", "mainFieldLabel")
	}, [selectedFormsConfig?.newRequestForm])

	const picklistsForItem = useMemo(() => {
		const getPicklistValues = () => {
			const itemId = selectedFormsConfig?.itemType?.id ?? undefined
			if (!itemId) return []

			const arr: TReactSelectOption[] = []
			formStore?.mainFieldsData?.map(
				(o) =>
					o.fieldType === "PICKLIST" &&
					arr.push({
						value: o.fieldId,
						label: o.name,
						extraProp: o?.itemOfTypeId,
					})
			)

			return arr
		}

		return getPicklistValues()
	}, [formStore?.mainFieldsData])

	return (
		<HyperDiv direction="column" spaceBetweenElems="2rem">
			{selectedFormsConfig?.newRequestForm &&
				selectedFormsConfig.newRequestForm.length > 0 &&
				selectedFormsConfig.newRequestForm?.map((data, i) => {
					// ! TECHNICAL DEBT: 3184, replace this with dynamic key ([parsedkey])
					let isEN = formStore.newRequestLanguage === "en"

					const { mainFieldKey, mainFieldLabel, nameLV, nameEN, tooltip, predicateFieldKey } = data

					const handleLanguageSelect = (
						nameText: string | undefined,
						tooltipNew: IToolTipText | undefined
					) => {
						if (isEN) {
							formStore.mergeNewRequestFormData(i, {
								// If no new value is received, then appends old value.
								nameEN: nameText ?? nameEN,
								tooltip: tooltipNew ?? tooltip,
							})
						} else {
							formStore.mergeNewRequestFormData(i, {
								// If no new value is received, then appends old value.
								nameLV: nameText ?? nameLV,
								tooltip: tooltipNew ?? tooltip,
							})
						}
					}

					const fieldData = getFieldDataByMainFieldKey(mainFieldKey, formStore.mainFieldsData ?? [])

					const predicateFieldData = selectedFormsConfig?.newRequestForm.filter(
						({ mainFieldKey }) => mainFieldKey === predicateFieldKey
					)

					const predicateFieldPicklistValues = predicateFieldData
						? predicateFieldData[0]?.pickListValues ?? []
						: []

					const pickListOptions = fieldData?.picklistValues
					const itemOfTypeOptions = fieldData?.itemOfTypeValues

					const itemOfTypeBooleanOptions = fieldData?.itemOfTypeBoolean?.map(
						({ name, fieldId, itemOfTypeId }: TItemOfTypeOtherValues) => ({
							value: fieldId,
							label: name,
							extraProp: itemOfTypeId,
						})
					)

					const itemOfTypeUserOptions = fieldData?.itemOfTypeUser?.map(
						({ name, fieldId, itemOfTypeId }: TItemOfTypeOtherValues) => ({
							value: fieldId,
							label: name,
							extraProp: itemOfTypeId,
						})
					)

					const picklistId = fieldData?.picklistId

					const {
						userDepField,
						userEmailField,
						userIdField,
						userMobField,
						userNameField,
						collections,
					} = selectedFormsConfig

					//Checks if field is added because of multi collection
					const isFieldAddedFormCollection = collections?.some(
						(item) => item.picklistFieldId === mainFieldKey
					)
					//If anonymous users then check if field is added from item
					const isFieldAddedFormItem =
						instanceAnonymousUsers && !!mainFieldKey
							? userDepField?.fieldId === mainFieldKey ||
							  userEmailField?.fieldId === mainFieldKey ||
							  userIdField?.fieldId === mainFieldKey ||
							  userMobField?.fieldId === mainFieldKey ||
							  userNameField?.fieldId === mainFieldKey
							: false

					// Must be required and NOT readonly or added from collection
					const isFieldUnconditionallyRequired =
						((fieldData?.required && !fieldData.readOnly) ||
							isFieldAddedFormCollection ||
							isFieldAddedFormItem) ??
						false

					const isUserSelected = selectedFormsConfig.newRequestForm?.some(
						({ itemOfTypeUserItemId }) => !!itemOfTypeUserItemId
					)

					return (
						<ToolFieldPropertiesHeaderWithContent
							key={mainFieldKey + i}
							label={mainFieldLabel}
							selectedLanguage={formStore.newRequestLanguage}
							titleInputValue={isEN ? nameEN : nameLV}
							buttonColors={formStore.settingsData.personalize}
							titleInputPlaceholder={t("rawText.fieldName")}
							tooltipPlaceholder={t("rawText.tooltip")}
							tooltip={tooltip}
							isContentHidden={isSectionHidden[mainFieldKey]}
							onClickMoveUp={() => formStore.moveFieldInForm("newRequestForm", i, i - 1)}
							onClickMoveDown={() => formStore.moveFieldInForm("newRequestForm", i, i + 1)}
							onClickRemove={() => formStore.deleteFormField("newRequestForm", i)}
							onClickLabel={() => toggleIsSectionHidden(mainFieldKey)}
							onChangeFormData={({ nameText, tooltip }) => {
								handleLanguageSelect(nameText, tooltip)
							}}
						>
							<ToolFieldProperties
								picklistId={picklistId}
								pickListOptions={pickListOptions ?? undefined}
								predicateFieldValuesOptions={predicateFieldPicklistValues ?? undefined}
								itemOfTypeOptions={itemOfTypeOptions ?? undefined}
								itemOfTypeBooleanOptions={itemOfTypeBooleanOptions ?? undefined}
								itemOfTypeUserOptions={itemOfTypeUserOptions ?? undefined}
								predicateFieldKeysOptions={predicateFieldKeysOptions}
								isFieldUnconditionallyRequired={isFieldUnconditionallyRequired}
								// Field type is uneditable value which comes from field name
								value={{
									...data,
									fieldType: fieldData?.fieldType ?? data.fieldType,
									itemOfTypeId: fieldData?.itemOfTypeId ?? undefined,
								}}
								onChangeFormData={(data) => formStore.mergeNewRequestFormData(i, data)}
								picklistsForItem={picklistsForItem}
								isUserSelected={isUserSelected}
							/>
						</ToolFieldPropertiesHeaderWithContent>
					)
				})}
		</HyperDiv>
	)
})

export default NewRequestForm
